import Box from '@mui/material/Box'
import { useLayoutEffect, useRef } from 'react'

// Keep the grid's viewport small; the surrounding page owns the scroll range.
export default function PageScrollContainer({ children }) {
  const pageRef = useRef(null)
  const viewportRef = useRef(null)
  const measureRef = useRef(null)

  useLayoutEffect(() => {
    // MUI creates its scroller after measuring the grid's initial layout.
    const initialize = () => {
      const page = pageRef.current
      const viewport = viewportRef.current
      const grid = viewport.querySelector('.MuiDataGrid-root')
      const scroller = viewport.querySelector('.MuiDataGrid-virtualScroller')
      const content = viewport.querySelector(
        '.MuiDataGrid-virtualScrollerContent'
      )
      let scrollParent = page.parentElement
      while (
        scrollParent &&
        !/(auto|scroll|overlay)/.test(getComputedStyle(scrollParent).overflowY)
      ) {
        scrollParent = scrollParent.parentElement
      }
      if (
        scrollParent === document.body ||
        scrollParent === document.documentElement
      ) {
        scrollParent = null
      }
      const scrollTarget = scrollParent || window
      const getViewportTop = () =>
        scrollParent
          ? scrollParent.getBoundingClientRect().top + scrollParent.clientTop
          : 0
      let lastGridTop = scroller.scrollTop
      let frame

      const syncFromPage = () => {
        // A keyboard scroll can precede a queued page-scroll event.
        if (Math.abs(scroller.scrollTop - lastGridTop) >= 1) {
          syncFromGrid()
          return
        }
        const top = Math.max(
          0,
          getViewportTop() - page.getBoundingClientRect().top
        )
        lastGridTop = Math.min(
          top,
          Math.max(0, scroller.scrollHeight - scroller.clientHeight)
        )
        if (scroller.scrollTop !== lastGridTop) scroller.scrollTop = lastGridTop
      }

      // Keyboard navigation can scroll the grid itself; move the page with it.
      const syncFromGrid = () => {
        if (Math.abs(scroller.scrollTop - lastGridTop) < 1) return
        lastGridTop = scroller.scrollTop
        const delta =
          lastGridTop - (getViewportTop() - page.getBoundingClientRect().top)
        if (scrollParent) scrollParent.scrollTop += delta
        else window.scrollBy(0, delta)
      }

      const measure = () => {
        const contentHeight =
          content.offsetHeight || parseFloat(content.style.height) || 0
        const chromeHeight = grid.offsetHeight - scroller.clientHeight
        const totalHeight = Math.max(300, contentHeight + chromeHeight)
        page.style.height = `${totalHeight}px`
        viewport.style.height = `${Math.min(
          scrollParent?.clientHeight || window.innerHeight,
          totalHeight
        )}px`
        syncFromPage()
      }
      const scheduleMeasure = () => {
        cancelAnimationFrame(frame)
        frame = requestAnimationFrame(measure)
      }
      measureRef.current = scheduleMeasure
      const observer =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(scheduleMeasure)
          : null
      observer?.observe(content)
      observer?.observe(viewport)
      if (scrollParent) observer?.observe(scrollParent)
      scrollTarget.addEventListener('scroll', syncFromPage, { passive: true })
      scroller.addEventListener('scroll', syncFromGrid, { passive: true })
      window.addEventListener('resize', scheduleMeasure)
      measure()

      return () => {
        measureRef.current = null
        cancelAnimationFrame(frame)
        observer?.disconnect()
        scrollTarget.removeEventListener('scroll', syncFromPage)
        scroller.removeEventListener('scroll', syncFromGrid)
        window.removeEventListener('resize', scheduleMeasure)
      }
    }
    let disconnect
    const initialFrame = requestAnimationFrame(() => {
      disconnect = initialize()
    })
    return () => {
      cancelAnimationFrame(initialFrame)
      disconnect?.()
    }
  }, [])

  // Filters and loading notices can move the table without resizing it.
  useLayoutEffect(() => {
    measureRef.current?.()
  })

  return (
    <Box ref={pageRef} data-testid="page-scroll-grid">
      <Box
        ref={viewportRef}
        sx={{
          position: 'sticky',
          top: 0,
          height: 400,
          '& .MuiDataGrid-virtualScroller': { overflowY: 'hidden' }
        }}
      >
        {children}
      </Box>
    </Box>
  )
}
